import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { getArticleContent, useArticleContent } from '@/hooks/useArticleContent';
import JsonLd from '@/components/base/JsonLd';
import { siteUrl, siteName, siteLogo } from '@/lib/seo';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug = 'usfans-spreadsheet-guide' } = useParams();
  const content = useArticleContent(slug);
  const meta = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${siteUrl}/article/${slug}#article`,
        headline: content.title,
        description: content.desc,
        image: meta?.image,
        datePublished: meta?.updatedDate,
        dateModified: meta?.updatedDate,
        author: {
          '@type': 'Organization',
          name: siteName,
          url: `${siteUrl}/`,
        },
        publisher: {
          '@type': 'Organization',
          name: siteName,
          logo: {
            '@type': 'ImageObject',
            url: siteLogo,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/article/${slug}`,
        },
        inLanguage: i18n.language?.split('-')[0] ?? 'en',
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/article/${slug}#faq`,
        mainEntity: content.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />

      <main className="pt-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
              {t('nav.home')}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{content.category}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              {content.category}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-950">
              {content.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {t('articles.readTime', { minutes: meta?.readingTime ?? 8 })}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {t('article.updated')}: {meta?.updatedDate ?? '2026-08-21'}
              </span>
            </div>
          </header>

          {/* Article body */}
          <article className="mt-8 space-y-10">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article.intro.title')}</h2>
              {content.intro.map((para, i) => (
                <p key={i} className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                  {para}
                </p>
              ))}
            </section>

            {content.sections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{section.heading}</h2>
                {section.paragraphs.map((para, j) => (
                  <p key={j} className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                    {para}
                  </p>
                ))}
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {section.bullets.map((b, k) => (
                      <li key={k} className="flex items-start gap-2 rounded-md border border-background-200 bg-background-50 p-3">
                        <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" />
                        <span className="text-sm text-foreground-700 leading-snug">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article.faq.title')}</h2>
              <div className="mt-5 flex flex-col gap-4">
                {content.faq.map((item, i) => (
                  <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <p className="flex items-start gap-2 text-sm md:text-base font-semibold text-foreground-900">
                      <i className="ri-question-line mt-0.5 text-primary-600" />
                      {item.q}
                    </p>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related guides (internal links) */}
            {related.length > 0 && (
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('articles.related')}</h2>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {related.map((rel) => {
                    const relContent = getArticleContent(i18n.language, rel.slug);
                    return (
                      <Link
                        key={rel.slug}
                        to={`/article/${rel.slug}`}
                        className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
                      >
                        <div className="relative h-28 w-full overflow-hidden rounded-md">
                          <img
                            src={rel.image}
                            alt={relContent.title}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <span className="mt-3 text-xs font-medium text-secondary-700">{relContent.category}</span>
                        <span className="mt-1 text-sm font-semibold text-foreground-900 leading-snug">
                          {relContent.title}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Internal link back to homepage */}
            <section className="rounded-lg bg-primary-500 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">{content.ctaTitle}</h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-background-50/90">{content.ctaDesc}</p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50 px-5 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  {t('article.cta.back')}
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('article.cta.store')}
                  <i className="ri-arrow-right-up-line" />
                </a>
              </div>
            </section>

            <p className="text-xs text-foreground-500 leading-relaxed">{t('article.disclaimer')}</p>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}